import { useMemo, useState } from "react";
import SectionHeading from "@/pages/home/components/SectionHeading";
import { comicCategories, comics } from "@/mocks/doumanData";

export default function ComicSection() {
  const [category, setCategory] = useState("all");
  const [keyword, setKeyword] = useState("");

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return comics.filter((comic) => {
      const matchCategory = category === "all" || comic.category === category;
      const matchKeyword =
        kw === "" ||
        comic.title.toLowerCase().includes(kw) ||
        comic.categoryName.toLowerCase().includes(kw) ||
        comic.tags.some((tag) => tag.toLowerCase().includes(kw));
      return matchCategory && matchKeyword;
    });
  }, [category, keyword]);

  return (
    <section id="comics" className="scroll-mt-20 bg-background-100 py-16 md:py-24">
      <div className="px-4 md:px-8">
        <SectionHeading
          eyebrow="漫画资源"
          title="热门题材速览，按分类快速找到想看的类型"
          desc="平台内容通常按题材分类整理。下面按七个常见大类筛选示例作品，帮你了解各类型的大致风格与更新状态。"
        />

        <div className="mt-10 flex flex-col lg:flex-row lg:items-center gap-4 lg:justify-between">
          <div className="inline-flex flex-wrap p-1 rounded-full bg-background-50 border border-background-200" role="tablist" aria-label="漫画分类">
            {comicCategories.map((cat) => (
              <button
                key={cat.id}
                type="button"
                role="tab"
                aria-selected={category === cat.id}
                onClick={() => setCategory(cat.id)}
                className={`px-3.5 py-1.5 rounded-full text-sm font-bold transition-colors cursor-pointer whitespace-nowrap ${
                  category === cat.id
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-600 hover:text-primary-600"
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          <div className="relative w-full lg:w-72">
            <i
              className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base"
              aria-hidden="true"
            ></i>
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索题材或标签，如：治愈"
              aria-label="搜索漫画题材"
              className="w-full pl-9 pr-3 py-2.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
            />
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="mt-12 py-16 text-center rounded-lg bg-background-50 border border-background-200">
            <i className="ri-emotion-sad-line text-4xl text-foreground-300" aria-hidden="true"></i>
            <p className="mt-3 text-sm text-foreground-600">没有找到匹配的内容，换个关键词或分类试试。</p>
          </div>
        ) : (
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {filtered.map((comic) => (
              <article key={comic.id} className="rounded-lg bg-background-50 border border-background-200 overflow-hidden">
                <div className="flex gap-4 p-5">
                  <div className="shrink-0 w-24 h-32 rounded-md overflow-hidden bg-background-200">
                    <img src={comic.cover} alt={`${comic.title}漫画封面`} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="min-w-0 flex flex-col">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 text-xs font-bold">
                        {comic.categoryName}
                      </span>
                      <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-900 text-xs font-bold">
                        {comic.status}
                      </span>
                    </div>
                    <h3 className="mt-2 font-heading font-bold text-base text-foreground-950 truncate">{comic.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-foreground-600 line-clamp-3">{comic.summary}</p>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {comic.tags.map((tag) => (
                        <span key={tag} className="px-2 py-0.5 rounded bg-background-100 text-foreground-600 text-xs">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="px-5 py-3 border-t border-background-200 flex items-center justify-between text-xs text-foreground-500">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-3-line" aria-hidden="true"></i>
                    {comic.author}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-bookmark-line" aria-hidden="true"></i>
                    加入书架
                  </span>
                </div>
              </article>
            ))}
          </div>
        )}

        <p className="mt-8 text-xs leading-relaxed text-foreground-500 text-center">
          说明：以上名称为题材示例，仅用于展示分类与筛选效果，不代表平台实际收录作品，具体以平台内页面为准。
        </p>
      </div>
    </section>
  );
}